import { Fragment, useMemo, useState } from 'react';
import { BarChart3, ChevronRight, Users } from 'lucide-react';
import {
  bilanDeLaClasse, comprehensionComparee, comprehensionDeLaClasse, elevesDuQcm, nomCourt, SEUIL_COPIES_CLASSE,
} from './margeDuHasard';
import { COMPETENCES_ORDRE } from './progressionParCompetence';
import { Carte, Pastille, PARTIES } from './ElementsProgression';

/**
 * L'écran d'un QCM de suivi, À L'ESSAI (12/10/2026, épreuve de Musk, à la place de la vue par domaine et de ses
 * marges). Lisible en quelques secondes entre deux cours :
 * - la classe, une tuile par partie (Lexique, Automatismes…, et la compréhension sur d'autres textes, bien à part) :
 *   la moyenne de septembre et celle d'aujourd'hui en deux barres, l'écart, et un mot (en progrès, pas de
 *   changement net, en recul) décidé par la marge du hasard, qui ne s'affiche pas ; pas de mot sous cinq élèves ;
 * - chaque élève, du plus grand progrès au plus grand recul, une colonne par partie, sans verdict (sur quelques
 *   questions, deux réponses devinées font un progrès) ; ses questions d'un clic. Un item posé en plusieurs
 *   variantes compte la part de ses variantes justes ; la compréhension se compare compétence par compétence
 *   (comprehensionComparee : les comptes à plan égal, sinon la moyenne de ses taux).
 * Chaque item de septembre est un savoir (une variante garde l'identité de son item). L'exception : la
 * compréhension sur d'autres textes (forme parallèle) n'a pas d'item à reprendre, elle se lit par compétence, sur
 * le même type de texte qu'en septembre.
 */
const NB = ' ';
const MOINS = '−';
const MOT = { progres: 'en progrès', stable: 'pas de changement net', recul: 'en recul' };
const STYLE_MOT = {
  progres: 'bg-emerald-100 text-emerald-700',
  stable: 'bg-gray-100 text-gray-600',
  recul: 'bg-red-100 text-red-700',
};
const COMPETENCES = { explicite: 'Information explicite', inference: 'Inférences', sens_global: 'Sens global' };
const SUPPORTS = { litteraire: 'texte littéraire', documentaire: 'support documentaire' };

const AUTRES_TEXTES = 'autres_textes';
const PARTIE_AUTRES_TEXTES = { ...PARTIES.comprehension, nom: "Compréhension de l'écrit" };

const pourcent = (x) => `${Math.round(x)}${NB}%`;
// Un item posé en plusieurs variantes compte la part de ses variantes justes : 3 sur 4, 0,75
const nombre = (x) => (Math.abs(x - Math.round(x)) < 1e-9 ? String(Math.round(x)) : x.toFixed(2).replace(/0+$/, '').replace('.', ','));
const fraction = (c) => `${nombre(c.justes)}/${c.total}`;
const lu = (score) => (score === null ? 'pas de réponse' : score === 1 ? 'juste' : 'faux');

/** « ▲ +13 pts », « ▼ −2 », « = » : en questions si les deux totaux sont égaux, sinon en points. */
const Ecart = ({ ecart, unite = '' }) => {
  if (ecart > 1e-9) return <span className="font-semibold text-emerald-700">▲{NB}+{nombre(ecart)}{unite}</span>;
  if (ecart < -1e-9) return <span className="font-semibold text-red-700">▼{NB}{MOINS}{nombre(Math.abs(ecart))}{unite}</span>;
  return <span className="font-semibold text-gray-400">={NB}0{unite}</span>;
};

const Barre = ({ libelle, taux, couleur, fort = false }) => (
  <div className="flex items-center gap-3">
    <span className="w-20 shrink-0 text-xs text-gray-500">{libelle}</span>
    <div className="flex-1 h-2.5 rounded-full bg-gray-100 overflow-hidden">
      <div className={`h-full rounded-full ${couleur}`} style={{ width: `${Math.max(0, Math.min(100, taux))}%` }} />
    </div>
    <span className={`w-12 shrink-0 text-right text-sm tabular-nums ${fort ? 'font-semibold text-gray-900' : 'text-gray-500'}`}>{pourcent(taux)}</span>
  </div>
);

/** Une mesure de la classe : son nom, l'écart et le mot, puis septembre et aujourd'hui en deux barres. */
const Mesure = ({ nom, ligne, couleur }) => (
  <div className="space-y-1.5">
    <div className="flex flex-wrap items-center gap-2">
      {nom ? <span className="text-sm font-medium text-gray-800">{nom}</span> : null}
      {/* L'écart des deux taux affichés, arrondis (sinon 46 % → 49 % pouvait dire +4) */}
      <span className="ml-auto text-sm tabular-nums"><Ecart ecart={Math.round(ligne.tauxApres) - Math.round(ligne.tauxAvant)} unite={`${NB}pts`} /></span>
      {ligne.nbEleves >= SEUIL_COPIES_CLASSE ? (
        <span className={`px-2 py-0.5 rounded-md text-xs font-semibold ${STYLE_MOT[ligne.verdict]}`}>{MOT[ligne.verdict]}</span>
      ) : null}
    </div>
    <Barre libelle="Septembre" taux={ligne.tauxAvant} couleur="bg-gray-300" />
    <Barre libelle="Aujourd'hui" taux={ligne.tauxApres} couleur={couleur} fort />
  </div>
);

// Une div, pas une section : index.css centre en flex toute <section>
const Tuile = ({ partie, nom, sousTitre, children }) => (
  <div role="group" aria-label={nom} className="rounded-xl border border-gray-100 bg-white p-4 space-y-3">
    <div className="space-y-1">
      <Pastille partie={partie} nom={nom} />
      <p className="text-xs text-gray-500">{sousTitre}</p>
    </div>
    {children}
  </div>
);

const CarteClasse = ({ classe, comprehension }) => {
  const nbEleves = Math.max(classe?.nbEleves || 0, comprehension?.nbEleves || 0);
  const peu = nbEleves < SEUIL_COPIES_CLASSE;
  return (
    <Carte icone={BarChart3} titre="La classe" aside={`septembre → aujourd'hui, ${nbEleves}${NB}élève${nbEleves > 1 ? 's' : ''} avec les deux copies`}>
      <div className="p-5 space-y-4">
        <div className="grid items-start gap-4 lg:grid-cols-2">
          {(classe?.domaines || []).map((d) => (
            <Tuile
              key={d.domaine.cle}
              partie={PARTIES[d.domaine.cle]}
              nom={d.domaine.nom}
              sousTitre={`Les mêmes questions qu'en septembre (ou leurs variantes)${NB}: ${d.nbQuestions}${NB}question${d.nbQuestions > 1 ? 's' : ''}`}
            >
              <Mesure ligne={d} couleur={PARTIES[d.domaine.cle].barre} />
            </Tuile>
          ))}
          {comprehension ? (
            <Tuile
              partie={PARTIE_AUTRES_TEXTES}
              nom={PARTIE_AUTRES_TEXTES.nom}
              sousTitre={`D'autres textes qu'en septembre (${comprehension.textes.map((t) => `«${NB}${t}${NB}»`).join(', ')})${NB}: comparée par compétence, au ${comprehension.supports.map((s) => SUPPORTS[s]).join(' et au ')} de septembre`}
            >
              {comprehension.competences.map((c) => (
                <Mesure key={c.cle} nom={COMPETENCES[c.cle]} ligne={c} couleur={PARTIE_AUTRES_TEXTES.barre} />
              ))}
            </Tuile>
          ) : null}
        </div>
        {peu ? (
          <p className="text-sm text-gray-600">
            {nbEleves === 1
              ? `Un seul élève a les deux copies${NB}: ces taux sont les siens, trop peu pour juger la classe.`
              : `Seulement ${nbEleves}${NB}élèves ont les deux copies${NB}: trop peu pour juger la classe.`}
          </p>
        ) : null}
      </div>
    </Carte>
  );
};

/** « 2/7 → 6/7 ▲ +4 » : sur les mêmes questions, l'écart en questions ; en points si les totaux diffèrent. */
const Evolution = ({ avant, apres }) => (
  <span className="inline-flex items-center gap-3 text-sm tabular-nums whitespace-nowrap">
    <span className="text-gray-500">
      {fraction(avant)} <span className="text-gray-400">→</span> <span className="font-semibold text-gray-900">{fraction(apres)}</span>
    </span>
    <Ecart ecart={apres.justes - avant.justes} />
  </span>
);

/** La compréhension quand les compétences ne sont pas posées dans les mêmes proportions : la moyenne de ses taux. */
const EvolutionTaux = ({ tauxAvant, tauxApres }) => (
  <span
    className="inline-flex items-center gap-3 text-sm tabular-nums whitespace-nowrap"
    title="La moyenne de ses compétences posées aujourd'hui, chacune pesant pareil"
  >
    <span className="text-gray-500">
      {pourcent(tauxAvant)} <span className="text-gray-400">→</span> <span className="font-semibold text-gray-900">{pourcent(tauxApres)}</span>
    </span>
    <Ecart ecart={Math.round(tauxApres) - Math.round(tauxAvant)} unite={`${NB}pts`} />
  </span>
);

/** La cellule d'une partie pour un élève : ses items avant puis maintenant, ou sa compréhension par compétence. */
const CelluleEleve = ({ eleve, partie }) => {
  if (partie.cle !== AUTRES_TEXTES) {
    const paire = eleve.paires.get(partie.cle);
    return paire ? <Evolution avant={paire.avant} apres={paire.apres} /> : <span className="text-gray-300">·</span>;
  }
  const comprehension = comprehensionComparee(eleve.comprehension);
  if (!comprehension) return <span className="text-gray-300">·</span>;
  return comprehension.memePlan
    ? <Evolution avant={comprehension.avant} apres={comprehension.apres} />
    : <EvolutionTaux tauxAvant={comprehension.tauxAvant} tauxApres={comprehension.tauxApres} />;
};

const Marque = ({ score, avant = false }) => {
  if (score === null) return <span className="text-gray-300">·</span>;
  if (score === 1) return <span className={avant ? 'text-emerald-500' : 'font-bold text-emerald-600'}>✓</span>;
  return <span className={avant ? 'text-red-400' : 'font-bold text-red-600'}>✗</span>;
};

const DetailItems = ({ eleve, colonnes }) => (
  <ul className="space-y-1">
    {colonnes.map((c) => {
      const reponse = eleve.cases.get(c.cle);
      return (
        <li key={c.cle} className="flex items-center gap-3 text-sm">
          <span className="w-24 shrink-0 text-xs font-bold text-gray-600">
            {nomCourt(c.domaine, c.domaine.cle === 'comprehension' ? c.cleTest : null, c.num)}
          </span>
          <span className="flex-1 min-w-0 truncate text-gray-600" title={c.libelle}>{c.libelle}</span>
          <span className="shrink-0 whitespace-nowrap" aria-hidden="true">
            <Marque score={reponse.sept} avant /> <span className="text-gray-400">→</span>{' '}
            {reponse.aujs.map((s, i) => <Fragment key={c.questions[i].id}>{i > 0 ? ' ' : ''}<Marque score={s} /></Fragment>)}
          </span>
          <span className="sr-only">
            septembre{NB}: {lu(reponse.sept)}, aujourd&apos;hui{NB}: {reponse.aujs.map(lu).join(', ')}
          </span>
        </li>
      );
    })}
  </ul>
);

const DetailCompetences = ({ comprehension }) => (
  <ul className="space-y-1">
    {COMPETENCES_ORDRE.map((cle) => {
      const avant = comprehension.septembre?.parCompetence[cle];
      const apres = comprehension.aujourdhui.parCompetence[cle];
      return (
        <li key={cle} className="flex items-center gap-3 text-sm">
          <span className="flex-1 text-gray-600">{COMPETENCES[cle]}</span>
          <span className="tabular-nums text-gray-500 whitespace-nowrap">
            {avant?.total ? fraction(avant) : '·'} <span className="text-gray-400">→</span>{' '}
            <span className="font-semibold text-gray-900">{apres?.total ? fraction(apres) : '·'}</span>
          </span>
        </li>
      );
    })}
  </ul>
);

const LigneEleve = ({ eleve, colonnes, parties }) => {
  const [ouvert, setOuvert] = useState(false);
  const basculer = () => setOuvert((v) => !v);
  return (
    <>
      <tr className={`border-t border-gray-100 cursor-pointer ${ouvert ? 'bg-gray-50' : 'hover:bg-gray-50'}`} onClick={basculer}>
        <th scope="row" className="px-5 py-2.5 text-left text-sm font-medium text-gray-800 whitespace-nowrap">
          <button
            type="button"
            aria-expanded={ouvert}
            onClick={(ev) => { ev.stopPropagation(); basculer(); }}
            className="inline-flex items-center gap-2 hover:text-blue-700"
          >
            <ChevronRight className={`w-4 h-4 text-gray-400 transition-transform ${ouvert ? 'rotate-90' : ''}`} aria-hidden="true" />
            {eleve.nom}
          </button>
          {eleve.aSeptembre ? null : <div className="pl-6 text-[11px] font-normal text-gray-400">pas de copie de septembre</div>}
        </th>
        {parties.map((p) => (
          <td key={p.cle} className={`px-4 py-2.5 ${p.cellule}`}>
            <CelluleEleve eleve={eleve} partie={p} />
          </td>
        ))}
      </tr>
      {ouvert ? (
        <tr className="bg-gray-50">
          <td colSpan={parties.length + 1} className="px-5 pb-4 pt-1">
            <div className="grid gap-x-8 gap-y-4 pl-6 lg:grid-cols-2">
              {parties.map((p) => (
                <div key={p.cle} className="space-y-1.5">
                  <Pastille partie={p} nom={p.nom} petite />
                  {p.cle === AUTRES_TEXTES
                    ? (eleve.comprehension ? <DetailCompetences comprehension={eleve.comprehension} /> : <p className="text-sm text-gray-400">pas de réponse</p>)
                    : <DetailItems eleve={eleve} colonnes={colonnes.filter((c) => c.domaine.cle === p.cle)} />}
                </div>
              ))}
            </div>
          </td>
        </tr>
      ) : null}
    </>
  );
};

const ProgressionParEleve = ({ evalNat, evalBilan, forme = null }) => {
  const { colonnes, eleves } = useMemo(() => elevesDuQcm({ evalNat, evalBilan, forme }), [evalNat, evalBilan, forme]);
  const classe = useMemo(() => bilanDeLaClasse({ evalNat, evalBilan }), [evalNat, evalBilan]);
  const comprehension = useMemo(() => comprehensionDeLaClasse({ evalNat, evalBilan, forme }), [evalNat, evalBilan, forme]);
  const parties = useMemo(() => {
    const domaines = [...new Map(colonnes.map((c) => [c.domaine.cle, c.domaine])).values()]
      .map((d) => ({ ...PARTIES[d.cle], cle: d.cle, nom: d.nom }));
    return eleves.some((e) => e.comprehension) ? [...domaines, { ...PARTIE_AUTRES_TEXTES, cle: AUTRES_TEXTES }] : domaines;
  }, [colonnes, eleves]);

  if (eleves.length === 0 || parties.length === 0) {
    return (
      <div className="bg-white rounded-2xl shadow-md border border-gray-100 px-5 py-6 text-sm text-gray-600">
        {eleves.length === 0
          ? "Aucun élève n'a encore rendu ce QCM."
          : `Aucune question de ce QCM ne reprend une question de septembre${NB}: rien à comparer.`}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {classe || comprehension ? <CarteClasse classe={classe} comprehension={comprehension} /> : null}
      <Carte icone={Users} titre="Les élèves" aside="du plus grand progrès au plus grand recul · un clic pour ses questions">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr>
                <th scope="col" className="px-5 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Élève</th>
                {parties.map((p) => (
                  <th key={p.cle} scope="col" className={`px-4 py-2.5 text-left ${p.cellule}`}>
                    <Pastille partie={p} nom={p.nom} petite />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {eleves.map((e) => <LigneEleve key={e.cle} eleve={e} colonnes={colonnes} parties={parties} />)}
            </tbody>
          </table>
        </div>
      </Carte>
    </div>
  );
};

export default ProgressionParEleve;
